import { useCallback, useId, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router';

import { isSameTree } from '@shared/block-editor';
import type { BlockTree } from '@shared/block-renderer';
import { UnsavedGuard } from '@shared/forms/UnsavedGuard';
import { Alert } from '@shared/ui/Alert';

import { editorCopy } from './copy';
import { PostEditorFlow, type FlowHandle } from './PostEditor.Flow';
import { PostEditorRail, type ArtworkFailure, type ArtworkSlot } from './PostEditor.Rail';
import type { ArtworkPolicy } from './queries';
import type { EditorialState, PostDraft, PostTag, RelatedTitle } from './types';

/** `content__editorial_posts.title` is `VARCHAR(255)`; the field says so first. */
const TITLE_MAX_LENGTH = 255;

/**
 * The canvas and the rail, shared by the two authoring routes.
 *
 * They are one component because the create route **is** the edit route empty
 * and fixed at draft — the pack says so in as many words, and two components
 * would agree today and drift at the first field.
 *
 * **The title is the first line written, not a field in a form.** It takes the
 * display face and the `h2` measure and loses the field's frame; what looks
 * like a field is filled once and forgotten, and what looks like a title is
 * reconsidered. The border returns on focus so a keyboard reader still knows
 * where the element ends.
 *
 * **The actions row sticks to the foot of the viewport.** A page written in
 * grows as long as what is written, and a save button that recedes with every
 * paragraph is the button that gets forgotten. Delete is pushed to the
 * reading-end edge so two actions — one of them without an easy way back — are
 * never adjacent in one group.
 */
export function PostEditor({
  state,
  stateHint,
  publishNotice,
  initialTitle,
  initialBody,
  authorName,
  tags,
  initialTagIds,
  relatedCandidates,
  onRelatedSearch,
  initialRelatedIds,
  relatedLabels,
  thumbnailUrl = null,
  heroArtworkUrl = null,
  onArtworkFile,
  artworkPolicy,
  artworkError = null,
  artworkBusy = false,
  bodyUneditable = false,
  busy = false,
  primaryLabel,
  onSave,
  onSubmitForReview,
  onDelete,
  banner,
}: {
  state: EditorialState;
  stateHint: string;
  publishNotice?: { title: string; body: string };
  initialTitle: string;
  initialBody: BlockTree;
  /** Who the post WILL be authored by — the service reads it off the token. */
  authorName: string;
  tags: readonly PostTag[];
  initialTagIds: readonly string[];
  relatedCandidates: readonly RelatedTitle[];
  /** The picker's term, for the server-side search behind it. */
  onRelatedSearch: (term: string) => void;
  initialRelatedIds: readonly string[];
  relatedLabels: Readonly<Record<string, string>>;
  thumbnailUrl?: string | null;
  heroArtworkUrl?: string | null;
  /** Absent on the create route — artwork registers against a post that exists. */
  onArtworkFile?: ((file: File, slot: ArtworkSlot) => void) | undefined;
  /** The published upload contract, so the rail states read numbers. */
  artworkPolicy?: ArtworkPolicy | undefined;
  /** An artwork refusal belongs beside the control that raised it, not in `banner`. */
  artworkError?: ArtworkFailure | null;
  artworkBusy?: boolean;
  /** The payload carries a body shape this editor cannot hold — see `./body.ts`. */
  bodyUneditable?: boolean;
  busy?: boolean;
  primaryLabel: string;
  onSave: (draft: PostDraft) => void;
  /** Absent on the create route: nobody hands over text typed in one sitting. */
  onSubmitForReview?: (draft: PostDraft) => void;
  /** Absent on the create route: there is nothing yet to delete. */
  onDelete?: () => void;
  /** A failure the surface wants above the actions rather than beside a field. */
  banner?: ReactNode;
}) {
  const titleId = useId();
  const titleErrorId = useId();
  const canvasHeadingId = useId();

  const flowRef = useRef<FlowHandle>(null);
  const titleRef = useRef<HTMLInputElement>(null);

  const [title, setTitle] = useState(initialTitle);
  const [tagIds, setTagIds] = useState<string[]>([...initialTagIds]);
  const [relatedIds, setRelatedIds] = useState<string[]>([...initialRelatedIds]);
  const [titleTouched, setTitleTouched] = useState(false);
  const [saving, setSaving] = useState(false);

  /**
   * Whether anything typed here would be lost by leaving.
   *
   * Read at the moment of departure rather than tracked continuously, because
   * the body is held in the DOM by the flow rather than in React state — see
   * `UnsavedGuard` and the flow's own note on why intercepting keystrokes is
   * not an option.
   *
   * `saving` suppresses it: the save itself navigates on success, and prompting
   * an operator about work that was just written is a prompt that teaches them
   * to dismiss the one that matters.
   */
  const isDirty = useCallback(() => {
    if (saving) return false;
    if (title !== initialTitle) return true;
    if (tagIds.length !== initialTagIds.length || tagIds.some((id, index) => id !== initialTagIds[index])) {
      return true;
    }
    if (
      relatedIds.length !== initialRelatedIds.length ||
      relatedIds.some((id, index) => id !== initialRelatedIds[index])
    ) {
      return true;
    }
    return !isSameTree(flowRef.current?.read() ?? initialBody, initialBody);
  }, [saving, title, initialTitle, tagIds, initialTagIds, relatedIds, initialRelatedIds, initialBody]);

  const titleError =
    titleTouched && title.trim().length === 0
      ? editorCopy.validation.titleRequired
      : title.length > TITLE_MAX_LENGTH
        ? editorCopy.validation.titleTooLong
        : undefined;

  /**
   * Read the form, or refuse and **put the caret where the problem is**.
   *
   * The refusal used to be silent in the way that matters: the message rendered
   * and `aria-invalid` was set, but focus stayed on the button that had just
   * been pressed. A keyboard or screen-reader user was told something was wrong
   * and left standing on the control that told them, with no indication of which
   * field to go to — and on a page long enough to scroll, a sighted user could
   * not see the message either.
   */
  function collect(): PostDraft | null {
    setTitleTouched(true);
    if (title.trim().length === 0 || title.length > TITLE_MAX_LENGTH) {
      titleRef.current?.focus();
      titleRef.current?.scrollIntoView({ block: 'center' });
      return null;
    }

    return {
      title,
      tagIds,
      relatedItemIds: relatedIds,
      // Read out of the live surface, never out of React state — the flow's
      // own note explains why its text never lives here.
      body: flowRef.current?.read() ?? initialBody,
    };
  }

  return (
    <>
      <UnsavedGuard dirty={isDirty} subject={editorCopy.unsaved} />
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          const draft = collect();
          if (draft) {
            setSaving(true);
            onSave(draft);
          }
        }}
      >
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_20rem] xl:items-start">
          <div className="min-w-0">
            <h2 id={canvasHeadingId} className="sr-only">
              {editorCopy.canvasHeading}
            </h2>

            <div className="mb-5">
              <label htmlFor={titleId} className="sr-only">
                {editorCopy.titleLabel}
              </label>
              <input
                ref={titleRef}
                id={titleId}
                type="text"
                value={title}
                disabled={busy}
                aria-required="true"
                aria-invalid={titleError ? true : undefined}
                aria-describedby={titleError ? titleErrorId : undefined}
                placeholder={editorCopy.titlePlaceholder}
                onChange={(event) => setTitle(event.target.value)}
                onBlur={() => setTitleTouched(true)}
                className="w-full rounded-sm border border-transparent bg-transparent px-2 py-3 font-display text-h2 font-bold leading-heading text-ink placeholder:text-ink-faint hover:border-border focus-visible:border-accent focus-visible:bg-surface"
              />
              {titleError ? (
                <p id={titleErrorId} className="mb-0 mt-2 max-w-none text-xs text-danger">
                  {titleError}
                </p>
              ) : null}
            </div>

            {bodyUneditable ? (
              <Alert kind="warning">{editorCopy.uneditableBody}</Alert>
            ) : (
              <PostEditorFlow
                ref={flowRef}
                initialTree={initialBody}
                disabled={busy}
                onDirty={() => undefined}
              />
            )}
          </div>

          <PostEditorRail
            state={state}
            thumbnailUrl={thumbnailUrl}
            heroArtworkUrl={heroArtworkUrl}
            onArtworkFile={onArtworkFile}
            artworkPolicy={artworkPolicy}
            artworkError={artworkError}
            artworkBusy={artworkBusy}
            stateHint={stateHint}
            {...(publishNotice ? { publishNotice } : {})}
            authorName={authorName}
            tags={tags}
            tagIds={tagIds}
            onTagsChange={setTagIds}
            relatedCandidates={relatedCandidates}
            onRelatedSearch={onRelatedSearch}
            relatedIds={relatedIds}
            onRelatedChange={setRelatedIds}
            relatedLabels={relatedLabels}
            disabled={busy}
          />
        </div>

        {banner ? <div className="mt-4">{banner}</div> : null}

        {/*
        **Full-width stack below `--bp-sm`, one row from it, and that is the
        pack's own rule rather than a preference.** `[data-form-actions] .btn`
        carries `inline-size: 100%` at the base tier and `auto` from 480 px, and
        the reason is stated there: two buttons sharing a line on a 320 px window
        drop below the 2.75 rem touch target. The console had been drawing four
        content-width buttons that wrapped two-and-two on a phone, with the
        destructive one thrown to the opposite edge of the second line by an
        `ms-auto` written for a row that was no longer a row — which is the
        arrangement that read as broken.

        The buttons keep the pack's order in the stack, so the destructive one is
        last on the phone and furthest from the primary. From `--bp-sm` the
        `ms-auto` becomes a row again and pushes it to the far end.
      */}
        <div className="mt-6 grid gap-3 border-t border-border bg-bg py-4 sm:flex sm:flex-wrap md:sticky md:z-40 md:[inset-block-end:0]">
          <button
            type="submit"
            disabled={busy || bodyUneditable}
            className="w-full rounded-sm bg-accent px-5 py-3 text-sm font-semibold text-accent-ink hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-45 sm:w-auto"
          >
            {primaryLabel}
          </button>

          {onSubmitForReview ? (
            <button
              type="button"
              disabled={busy || bodyUneditable}
              onClick={() => {
                const draft = collect();
                if (draft) {
                  setSaving(true);
                  onSubmitForReview(draft);
                }
              }}
              className="w-full rounded-sm border border-border-strong bg-surface-raised px-5 py-3 text-sm font-semibold text-ink hover:bg-surface-overlay disabled:cursor-not-allowed disabled:opacity-45 sm:w-auto"
            >
              {editorCopy.actions.submit}
            </button>
          ) : null}

          {/* «ألغِ» is secondary, not a ghost: a ghost with no fill and no border
            all but disappears on the dark canvas, and an alternative the eye
            cannot find is not an alternative. */}
          <Link
            to="/editorial"
            className="flex w-full items-center justify-center rounded-sm border border-border-strong bg-surface-raised px-5 py-3 text-sm font-semibold text-ink no-underline hover:bg-surface-overlay sm:w-auto"
          >
            {editorCopy.actions.cancel}
          </Link>

          {onDelete ? (
            <button
              type="button"
              disabled={busy}
              onClick={onDelete}
              className="w-full rounded-sm border border-danger px-5 py-3 text-sm font-semibold text-danger hover:bg-danger-soft disabled:cursor-not-allowed disabled:opacity-45 sm:ms-auto sm:w-auto"
            >
              {editorCopy.actions.delete}
            </button>
          ) : null}
        </div>
      </form>
    </>
  );
}
